import VenuePromotions from '../components/VenuePromotions';
import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { ArrowLeft, Plus, X, Clock, Pencil } from 'lucide-react';
import toast from 'react-hot-toast';
import api from '../services/api';
import { getVenueById } from '../services/venueService';
import { formatCurrency } from '../utils/formatters';

const DAYS = ['All', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];
const EMPTY_FORM = {
  courtId: '',
  dayOfWeek: 'All',
  startTime: '08:00',
  endTime: '18:00',
  pricePerHour: '',
  priority: 0,
};

export default function PricingManagement() {
  const { id: venueId } = useParams();
  const navigate = useNavigate();
  const [venue, setVenue] = useState(null);
  const [rules, setRules] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState(EMPTY_FORM);
  const [editingRuleId, setEditingRuleId] = useState(null);
  const [saving, setSaving] = useState(false);
  const [deletingRuleId, setDeletingRuleId] = useState(null);
  const formRef = React.useRef(null);

  function closeForm() {
    setShowForm(false); setEditingRuleId(null); setForm(EMPTY_FORM);
  }

  function editRule(rule) {
    setEditingRuleId(rule.id);
    setForm({ courtId: rule.courtId || '', dayOfWeek: rule.dayOfWeek,
      startTime: String(rule.startTime || '').slice(0, 5), endTime: String(rule.endTime || '').slice(0, 5),
      pricePerHour: String(rule.pricePerHour), priority: rule.priority ?? 0 });
    setShowForm(true);
  }

  useEffect(() => {
    if (showForm) {
      formRef.current?.scrollIntoView?.({ behavior: 'smooth', block: 'nearest' });
      formRef.current?.querySelector('select')?.focus();
    }
  }, [showForm, editingRuleId]);

  useEffect(() => {
    let active = true;
    setLoading(true); closeForm();
    Promise.all([getVenueById(venueId), api.get(`/venues/${venueId}/pricing`)])
      .then(([venueData, rulesData]) => {
        if (!active) return;
        setVenue(venueData.venue || venueData);
        setRules(Array.isArray(rulesData.data) ? rulesData.data : []);
      })
      .catch(() => { if (active) toast.error('Failed to load data'); })
      .finally(() => { if (active) setLoading(false); });
    return () => { active = false; };
  }, [venueId]);

  async function handleSaveRule(e) {
    e.preventDefault();
    if (saving) return;
    if (!form.pricePerHour || !Number.isFinite(Number(form.pricePerHour)) || Number(form.pricePerHour) <= 0) {
      toast.error('Enter a valid price');
      return;
    }
    if (!form.startTime || !form.endTime || form.startTime === form.endTime) {
      toast.error('Choose different start and end times'); return;
    }
    setSaving(true);
    try {
      const payload = {
        courtId: form.courtId || null,
        dayOfWeek: form.dayOfWeek,
        startTime: form.startTime,
        endTime: form.endTime,
        pricePerHour: parseFloat(form.pricePerHour),
        priority: parseInt(form.priority) || 0,
      };
      const res = editingRuleId
        ? await api.put(`/venues/${venueId}/pricing/${editingRuleId}`, payload)
        : await api.post(`/venues/${venueId}/pricing`, payload);
      setRules(prev => editingRuleId ? prev.map(rule => rule.id === editingRuleId ? res.data : rule) : [...prev, res.data]);
      toast.success(editingRuleId ? 'Pricing rule updated' : 'Pricing rule added');
      closeForm();
    } catch (error) { toast.error(error.response?.data?.message || `Failed to ${editingRuleId ? 'update' : 'add'} rule`); }
    finally { setSaving(false); }
  }

  async function handleDeleteRule(ruleId) {
    if (saving || deletingRuleId) return;
    setDeletingRuleId(ruleId);
    try {
      await api.delete(`/venues/${venueId}/pricing/${ruleId}`);
      setRules((prev) => prev.filter((r) => r.id !== ruleId));
      if (editingRuleId === ruleId) closeForm();
      toast.success('Pricing rule deleted');
    } catch { toast.error('Failed to delete rule'); }
    finally { setDeletingRuleId(null); }
  }

  function formatTime(time) {
    if (!time) return '';
    const [h, m] = (typeof time === 'string' ? time : time).split(':').map(Number);
    if (isNaN(h)) return time;
    const ampm = h >= 12 ? 'PM' : 'AM';
    const h12 = h === 0 ? 12 : h > 12 ? h - 12 : h;
    return `${h12}:${String(m || 0).padStart(2, '0')} ${ampm}`;
  }

  function isOvernight(startTime, endTime) {
    return Boolean(startTime && endTime && endTime < startTime);
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="h-8 w-8 animate-spin rounded-full border-4 border-[#264D59] border-t-transparent" />
      </div>
    );
  }

  const basePrice = venue?.pricePerHour || 0;

  return (
    <div className="space-y-6">
      <div className="flex items-center gap-4">
        <button onClick={() => navigate(-1)} className="btn-outline"><ArrowLeft className="h-4 w-4 mr-1" /> Back</button>
        <div>
          <h1 className="text-2xl font-bold text-[#1F2937]">Pricing &amp; promotions</h1>
          <p className="text-sm text-[#6B7280]">{venue?.name || 'Venue'} — Time-based pricing</p>
        </div>
      </div>

      <VenuePromotions venueId={venueId} courts={venue?.courts || []} />
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="lg:col-span-1 card p-6 space-y-4 h-fit">
          <h3 className="text-sm font-semibold text-[#1F2937] uppercase">Base Price</h3>
          <p className="text-3xl font-bold text-[#264D59]">{formatCurrency(basePrice)}<span className="text-sm text-[#6B7280] font-normal">/hr</span></p>
          <p className="text-xs text-[#6B7280]">Venue default when no court-specific or venue-wide rule matches.</p>
        </div>

        <div className="lg:col-span-2 card p-6 space-y-4">
          <div className="flex items-center justify-between">
            <div>
              <h3 className="text-sm font-semibold text-[#1F2937] uppercase">Court &amp; Time-Based Rules</h3>
              <p className="mt-1 text-xs text-[#6B7280]">Choose All courts for a venue-wide rate, or select one court to override it.</p>
            </div>
            <button disabled={saving || Boolean(deletingRuleId)} onClick={() => { setEditingRuleId(null); setForm(EMPTY_FORM); setShowForm(true); }} className="btn-primary">
              <Plus className="h-4 w-4 mr-1" /> Add Rule
            </button>
          </div>

          {showForm && (
            <form ref={formRef} aria-label={editingRuleId ? 'Edit pricing rule' : 'Add pricing rule'} onSubmit={handleSaveRule} className="p-4 bg-[#F1F5F9] rounded-[16px] space-y-4">
              <h4 className="font-semibold">{editingRuleId ? 'Edit pricing rule' : 'Add pricing rule'}</h4>
              <fieldset disabled={saving}>
              <div className="grid grid-cols-2 sm:grid-cols-3 xl:grid-cols-6 gap-3">
                <div>
                  <label htmlFor="pricing-court" className="block text-xs text-[#6B7280] mb-1">Applies to</label>
                  <select id="pricing-court" value={form.courtId} onChange={(e) => setForm((p) => ({ ...p, courtId: e.target.value }))} className="input-field text-sm">
                    <option value="">All courts</option>
                    {(venue?.courts || []).filter((court) => court.isActive || court.id === form.courtId).map((court) => <option key={court.id} value={court.id}>{court.name}</option>)}
                  </select>
                </div>
                <div>
                  <label htmlFor="pricing-day" className="block text-xs text-[#6B7280] mb-1">Day</label>
                  <select id="pricing-day" value={form.dayOfWeek} onChange={(e) => setForm((p) => ({ ...p, dayOfWeek: e.target.value }))} className="input-field text-sm">
                    {DAYS.map((d) => <option key={d} value={d}>{d}</option>)}
                  </select>
                </div>
                <div>
                  <label htmlFor="pricing-from" className="block text-xs text-[#6B7280] mb-1">From</label>
                  <input id="pricing-from" type="time" value={form.startTime} onChange={(e) => setForm((p) => ({ ...p, startTime: e.target.value }))} className="input-field text-sm" />
                </div>
                <div>
                  <label htmlFor="pricing-to" className="block text-xs text-[#6B7280] mb-1">To</label>
                  <input id="pricing-to" type="time" value={form.endTime} onChange={(e) => setForm((p) => ({ ...p, endTime: e.target.value }))} className="input-field text-sm" />
                  {isOvernight(form.startTime, form.endTime) && (
                    <p className="mt-1 text-xs font-medium text-[#264D59]">Next day</p>
                  )}
                </div>
                <div>
                  <label htmlFor="pricing-price" className="block text-xs text-[#6B7280] mb-1">Price/hr</label>
                  <input id="pricing-price" type="number" step="0.01" min="0" value={form.pricePerHour} onChange={(e) => setForm((p) => ({ ...p, pricePerHour: e.target.value }))} className="input-field text-sm" placeholder="100" />
                </div>
                <div>
                  <label htmlFor="pricing-priority" className="block text-xs text-[#6B7280] mb-1">Priority</label>
                  <input id="pricing-priority" type="number" min="0" value={form.priority} onChange={(e) => setForm((p) => ({ ...p, priority: e.target.value }))} className="input-field text-sm" placeholder="0" />
                </div>
              </div>
              </fieldset>
              <div className="flex justify-end gap-2">
                <button type="button" disabled={saving} onClick={closeForm} className="btn-outline text-sm">Cancel</button>
                <button type="submit" disabled={saving || Boolean(deletingRuleId)} className="btn-primary text-sm">{saving ? 'Saving…' : editingRuleId ? 'Save Changes' : 'Save Rule'}</button>
              </div>
            </form>
          )}

          {rules.length === 0 ? (
            <div className="text-center py-8">
              <Clock className="mx-auto h-8 w-8 text-[#94A3B8]" />
              <p className="mt-2 text-sm text-[#6B7280]">No pricing rules. The venue base rate applies to every court and hour.</p>
            </div>
          ) : (
            <div className="space-y-2">
              {rules.map((rule) => (
                <div key={rule.id} className="flex flex-wrap items-center justify-between gap-3 p-3 bg-[#F1F5F9] rounded-[12px]">
                  <div className="flex flex-wrap items-center gap-4">
                    <span className={`rounded-full px-2.5 py-1 text-xs font-semibold ${rule.courtId ? 'bg-[#DBEAFE] text-[#1D4ED8]' : 'bg-white text-[#475569]'}`}>
                      {rule.courtName || 'All courts'}
                    </span>
                    <span className="text-xs font-bold text-[#264D59] uppercase w-10">{rule.dayOfWeek}</span>
                    <span className="text-sm text-[#1F2937]">
                      {formatTime(rule.startTime)} — {formatTime(rule.endTime)}
                      {isOvernight(rule.startTime, rule.endTime) && <span className="text-xs text-[#64748B]"> (next day)</span>}
                    </span>
                    <span className="text-sm font-semibold text-[#56B7AC]">{formatCurrency(rule.pricePerHour)}<span className="text-xs text-[#6B7280] font-normal">/hr</span></span>
                    <span className="text-xs text-[#6B7280]">Priority: {rule.priority}</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <button type="button" disabled={saving || Boolean(deletingRuleId)} onClick={() => editRule(rule)} className="btn-outline text-xs px-3 py-1.5"><Pencil className="mr-1 h-3.5 w-3.5" />Edit</button>
                    <button type="button" aria-label="Delete pricing rule" disabled={saving || Boolean(deletingRuleId)} onClick={() => handleDeleteRule(rule.id)} className="text-[#D46C4E] hover:text-[#B3593E] p-1 transition-colors">
                      <X className="h-4 w-4" />
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
